import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTheme } from '../../context/ThemeContext';
import TextField from '../../components/formfields/TextField';
import RichSelectField from '../../components/formfields/RichSelectField';
import PhoneNumberField from '../../components/formfields/PhoneNumberField';
import EditableDisplayField from './EditableDisplayField';
import EditConfirmModal from './EditConfirmModal';
import { useFormStore } from '../../stores/formData';

const prefixField = (prefix, field) => {
  return prefix ? `${prefix}_${field}` : field;
};

const extractValue = (data) => {
  if (data && typeof data === 'object' && data.target && data.target.value !== undefined) {
    return data.target.value;
  }
  if (data && typeof data === 'object' && data.value !== undefined) {
    return data.value;
  }
  return data;
};

const AddressFormV2 = ({
  legIndex,
  addressType = 'origin',
  fieldPrefix = '',
  initialAddress = {},
  onAddressChange,
  onEditRequest,
  className = ''
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isMountedRef = useRef(false);

  const [address, setAddress] = useState(initialAddress);
  const [availableShips, setAvailableShips] = useState([]);
  const [availableItineraries, setAvailableItineraries] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFieldType, setEditFieldType] = useState(null);
  const [shouldNotifyParent, setShouldNotifyParent] = useState(false);
  
  const helpers = useFormStore((state) => state.helpers);
  const customerInfo = useFormStore((state) => state.formData.customerInfo);
  const onBehalfUser = useFormStore((state) => state.formData.onBehalfUser);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Determine if this is a cruise address
  const isCruiseAddress = useMemo(() => {
    return address.cruise === true;
  }, [address]);

  // Get cruise labels based on filter type
  const cruiseLabels = useMemo(() => {
    return {
      placeholder: address.cruiseFilter === 'tour_name' ? 'Select tour name' : 'Select ship',
      label: address.cruiseFilter === 'tour_name' ? 'Tour name' : 'Ship name',
      fieldName: address.cruiseFilter === 'ship_name' ? 'shipName' : 'tourName'
    };
  }, [address.cruiseFilter]);

  // Check if ship/tour is selected (for showing itinerary)
  const hasShipSelected = useMemo(() => {
    return !!(address.shipName || address.tourName);
  }, [address.shipName, address.tourName]);

  // Load available ships when cruise port is selected
  useEffect(() => {
    if (address.cruisePort && address.cruiseFilter) {
      const cruiseLine = address?.cruiseLine;
      if (!cruiseLine) return;

      const cruiseLineData = helpers.getCruiseData()?.find(line => line.id === cruiseLine.id);
      if (!cruiseLineData) return;

      const port = address.cruisePort;
      const matchedItineraries = cruiseLineData.cruise_sessions?.filter(itinerary => {
        // if cruise port is in the origin, we want to match iteneraries where the port is the disembarkation port
        // if cruise port is in the destination, we want to match iteneraries where the port is the embarkation port
        const matchDisembarkPort = addressType === 'origin' && itinerary.disembarkation_port_id === port.port_id;
        const matchEmbarkPort = addressType === 'destination' && itinerary.embarkation_port_id === port.port_id;
        return matchEmbarkPort || matchDisembarkPort;
      });

      const shipMap = {};
      matchedItineraries?.forEach(itinerary => {
        const shipName = itinerary[address.cruiseFilter];
        if (shipName) shipMap[shipName] = shipName;
      });
      setAvailableShips(Object.keys(shipMap));
    }
  }, [helpers, address.cruisePort, address.cruiseFilter, address.cruiseLine, addressType]);

  // Load available itineraries when ship is selected
  useEffect(() => {
    if (address.cruiseLine && hasShipSelected) {
      const cruiseData = helpers.getCruiseLineData(address.cruiseLine?.id);
      if (!cruiseData) return;

      const port = address.cruisePort;
      const matchedItineraries = cruiseData.cruise_sessions?.filter(itinerary => {
        const matchEmbarkPort = addressType === 'origin' && itinerary.disembarkation_port_id === port.port_id;
        const matchDisembarkPort = addressType === 'destination' && itinerary.embarkation_port_id === port.port_id;
        const matchedShip = itinerary[address.cruiseFilter] === address.shipName ||
          itinerary[address.cruiseFilter] === address.tourName;
        return matchedShip && (matchEmbarkPort || matchDisembarkPort);
      });
      setAvailableItineraries(matchedItineraries || []);
    }
  }, [helpers, 
    address.cruiseFilter,
    address.cruisePort,
    address.shipName,
    address.tourName,
    address.cruiseLine,
    hasShipSelected,
    addressType]);

  // Handle field changes
  const handleFieldChange = useCallback((field, value) => {
    if (!isMountedRef.current) return;
    const extractedValue = extractValue(value);

    setAddress(prev => {
      const updated = { ...prev, [field]: extractedValue };

      if (field === 'zip' && extractedValue !== prev.zip) {
        updated.zip = extractedValue;
        updated.zip5 = extractedValue;
        updated.postal_code = extractedValue;
      }

      if (field === 'cruiseLine' && extractedValue !== prev.cruiseLine) {
        updated.shipName = '';
        updated.tourName = '';
      }

      return updated;
    });
  }, []);

  // Save field on blur
  const handleSaveField = useCallback((field, value) => {
    setShouldNotifyParent(false);
    const extractedValue = extractValue(value);
    setAddress(prev => ({ ...prev, [field]: extractedValue }));
    setShouldNotifyParent(true);
  }, []);

  useEffect(() => {
    if (shouldNotifyParent && onAddressChange) {
      onAddressChange(address);
      setShouldNotifyParent(false);
    }
  }, [address, shouldNotifyParent, onAddressChange]);

  // Handle edit button click
  const handleEditClick = useCallback(() => {
    setEditFieldType(addressType);
    setShowEditModal(true);
  }, [addressType]);

  // Handle edit confirmation
  const handleEditConfirm = useCallback(() => {
    setShowEditModal(false);
    onEditRequest?.(legIndex, addressType);
  }, [addressType, onEditRequest]);

  // Get default name from customer info
  const defaultName = useMemo(() => {
    const firstName = onBehalfUser?.firstName || customerInfo?.firstName || '';
    const lastName = onBehalfUser?.lastName || customerInfo?.lastName || '';
    return `${firstName} ${lastName}`.trim();
  }, [onBehalfUser, customerInfo]);

  // Styling
  const containerClassName = clsx(
    'space-y-4',
    {
      'bg-slate-800': isDark,
      'bg-white': !isDark
    },
    className
  );

  const displayFieldsClassName = clsx(
    'flex flex-col gap-1 mb-4 pb-4',
    {
      'border-slate-700': isDark,
      'border-gray-200': !isDark
    }
  );

  const gridClassName = 'grid grid-cols-1 md:grid-cols-2 gap-4';
  const fullWidthClassName = 'md:col-span-2';

  return (
    <div className={containerClassName} data-testid={`${addressType}-address-form`}>
      {isCruiseAddress ? (
        // Cruise Address Layout
        <>
          {/* Display Fields - Port & Cruise Line */}
          <div className={displayFieldsClassName}>
            <EditableDisplayField
              label="Port"
              value={address?.cruisePort?.name || ''}
              onEdit={handleEditClick}
              isDark={isDark}
            />
            <EditableDisplayField
              label="Cruise line"
              value={address?.cruiseLine?.name || ''}
              onEdit={handleEditClick}
              isDark={isDark}
            />
          </div>

          {/* Ship Name & Name on Reservation */}
          <div className={gridClassName}>
            <RichSelectField
              id={prefixField(fieldPrefix, cruiseLabels.fieldName)}
              data-testid={prefixField(fieldPrefix, cruiseLabels.fieldName)}
              label={`${cruiseLabels.label}*`}
              value={address[cruiseLabels.fieldName] || ''}
              onChange={(value) => handleSaveField(cruiseLabels.fieldName, value)}
              placeholder={cruiseLabels.placeholder}
              options={availableShips}
              disabled={!address.cruiseLine}
              useCustomDropdown={true}
            />
            <TextField
              id={prefixField(fieldPrefix, 'nameOnReservation')}
              data-testid={prefixField(fieldPrefix, 'nameOnReservation')}
              label="Name on reservation*"
              placeholder="Enter name"
              value={address.nameOnReservation || defaultName}
              onChange={(value) => handleFieldChange('nameOnReservation', value)}
              onSaveOnBlur={() => handleSaveField('nameOnReservation', address.nameOnReservation)}
            />
          </div>

          {/* Reservation Number & Stateroom */}
          <div className={gridClassName}>
            <TextField
              id={prefixField(fieldPrefix, 'reservationNumber')}
              data-testid={prefixField(fieldPrefix, 'reservationNumber')}
              label="Reservation number (Optional)"
              placeholder="Optional"
              value={address.reservationNumber || ''}
              onChange={(value) => handleFieldChange('reservationNumber', value)}
              onSaveOnBlur={() => handleSaveField('reservationNumber', address.reservationNumber)}
            />
            <TextField
              id={prefixField(fieldPrefix, 'stateroom')}
              data-testid={prefixField(fieldPrefix, 'stateroom')}
              label="Stateroom (Optional)"
              placeholder="Optional"
              value={address.stateroom || ''}
              onChange={(value) => handleFieldChange('stateroom', value)}
              onSaveOnBlur={() => handleSaveField('stateroom', address.stateroom)}
            />
          </div>

          {/* Itinerary - Only shown when ship is selected */}
          {hasShipSelected && availableItineraries.length > 0 && (
            <div className={gridClassName}>
              <RichSelectField
                id={prefixField(fieldPrefix, 'selectedItinerary')}
                data-testid={prefixField(fieldPrefix, 'selectedItinerary')}
                label="Itinerary"
                value={address.selectedItinerary?.id || ''}
                onChange={(e) => {
                  const itinerary = availableItineraries.find(itin => itin.id === e.target.value);
                  handleSaveField('selectedItinerary', itinerary);
                }}
                placeholder="Select your itinerary"
                options={availableItineraries}
                valueKey="id"
                formatValue={(itinerary) => ({
                  id: itinerary.id,
                  key: itinerary.id,
                  label: `${itinerary.embarkation_date} - ${itinerary.disembarkation_date} - ${itinerary[address.cruiseFilter]} - ${itinerary.embarkation_port} to ${itinerary.disembarkation_port}`,
                })}
                containerClassName={fullWidthClassName}
                useCustomDropdown={true}
              />
            </div>
          )}
        </>
      ) : (
        // Standard Address Layout
        <>
          {/* Display Fields - City & State */}
          <div className={displayFieldsClassName}>
            <div className="flex flex-col gap-1">
              <EditableDisplayField
                label="City"
                value={address?.city || ''}
                onEdit={handleEditClick}
                isDark={isDark}
              />
              <EditableDisplayField
                label="State"
                value={address?.state || ''}
                onEdit={handleEditClick}
                isDark={isDark}
              />
            </div>
          </div>

          {/* Sender/Recipient Name & Phone */}
          <div className={gridClassName}>
            {addressType === 'origin' ? (
              <TextField
                id={prefixField(fieldPrefix, 'senderName')}
                data-testid={prefixField(fieldPrefix, 'senderName')}
                label="Sender name*"
                placeholder="Input sender name"
                value={address.senderName || defaultName}
                onChange={(value) => handleFieldChange('senderName', value)}
                onSaveOnBlur={() => handleSaveField('senderName', address.senderName)}
              />
            ) : (
              <TextField
                id={prefixField(fieldPrefix, 'recipientName')}
                data-testid={prefixField(fieldPrefix, 'recipientName')}
                label="Recipient name*"
                placeholder="Input recipient name"
                value={address.recipientName || defaultName}
                onChange={(value) => handleFieldChange('recipientName', value)}
                onSaveOnBlur={() => handleSaveField('recipientName', address.recipientName)}
              />
            )}
            <PhoneNumberField
              data-testid={prefixField(fieldPrefix, 'phoneNumber')}
              label="Phone number"
              placeholder="Input phone number"
              value={address.phoneNumber || onBehalfUser?.phoneNumber || customerInfo?.phoneNumber || ''}
              onChange={(value) => handleFieldChange('phoneNumber', value)}
              onBlur={() => handleSaveField('phoneNumber', address.phoneNumber)}
            />
          </div>

          {/* Address & Zip Code */}
          <div className={gridClassName}>
            <TextField
              id={prefixField(fieldPrefix, 'streetAddress')}
              data-testid={prefixField(fieldPrefix, 'streetAddress')}
              label="Address*"
              placeholder="Input address"
              disabled={true}
              value={address.streetAddress || address.name || address.street || ''}
              onChange={(value) => handleFieldChange('streetAddress', value)}
              onSaveOnBlur={() => handleSaveField('streetAddress', address.streetAddress)}
            />
            <TextField
              id={prefixField(fieldPrefix, 'zip')}
              data-testid={prefixField(fieldPrefix, 'zip')}
              label="Zip Code*"
              placeholder="Input zip code"
              disabled={true}
              value={address.zip || address.zip5 || address.postal_code || ''}
              onChange={(value) => handleFieldChange('zip', value)}
              onSaveOnBlur={() => handleSaveField('zip', address.zip)}
            />
          </div>

          {/* Company Name & Apt/Suite */}
          <div className={gridClassName}>
            <TextField
              id={prefixField(fieldPrefix, 'companyName')}
              data-testid={prefixField(fieldPrefix, 'companyName')}
              label="Company Name (Optional)"
              placeholder="Enter company name"
              value={address.companyName || ''}
              onChange={(value) => handleFieldChange('companyName', value)}
              onSaveOnBlur={() => handleSaveField('companyName', address.companyName)}
            />
            <TextField
              id={prefixField(fieldPrefix, 'address2')}
              data-testid={prefixField(fieldPrefix, 'address2')}
              label="Apt #, Suite, Floor, etc. (Optional)"
              placeholder="Enter apt, suite, etc."
              value={address.address2 || ''}
              onChange={(value) => handleFieldChange('address2', value)}
              onSaveOnBlur={() => handleSaveField('address2', address.address2)}
            />
          </div>
        </>
      )}

      {/* Edit Confirmation Modal */}
      <EditConfirmModal
        isOpen={showEditModal}
        fieldType={editFieldType}
        onConfirm={handleEditConfirm}
        onCancel={() => setShowEditModal(false)}
        isDark={isDark}
      />
    </div>
  );
};

AddressFormV2.propTypes = {
  legIndex: PropTypes.number.isRequired,
  addressType: PropTypes.oneOf(['origin', 'destination']),
  fieldPrefix: PropTypes.string,
  initialAddress: PropTypes.object,
  onAddressChange: PropTypes.func.isRequired,
  onEditRequest: PropTypes.func, // Called with 'origin' or 'destination' when user confirms edit
  className: PropTypes.string
};

export default AddressFormV2;